import { getCurrentInstance } from 'vue';

// Get translation function from Vue instance or return fallback
function getTranslator() {
  try {
    const instance = getCurrentInstance();
    if (instance?.appContext?.config?.globalProperties?.$t) {
      return instance.appContext.config.globalProperties.$t;
    }
  } catch (e) {
    // Fallback to English if not in Vue context
  }
  return null;
}

// Default English messages (fallback)
const defaultMessages = {
  required: 'This field is required.',
  maxLength: (max) => `Maximum length is ${max} characters.`,
  minLength: (min) => `Minimum length is ${min} characters.`,
  minItems: (min) => `Minimum number of items is ${min}.`,
  email: 'Invalid email address.',
  min: (min) => `Value must be at least ${min}.`,
  max: (max) => `Value must be no more than ${max}.`,
  requiredCheckbox: 'At least one option must be selected.',
  kvkNl: 'KVK-nummer moet 8 cijfers zijn.',
  vatNl: 'BTW-nummer moet de vorm NL000000000B00 hebben.',
};

export function validateInput(value, rules) {
  const errors = [];
  const $t = getTranslator();

  if (rules) {
    rules.forEach((rule) => {
      const [ruleName, ruleValue] = rule.split(':');
      switch (ruleName) {
        case 'required':
          if (
            !value ||
            (typeof value === 'string' &&
              // Check for empty string
              (!value.trim() ||
                // Check for empty HTML tags like <p></p>
                value.replace(/<[^>]*>/g, '').trim().length === 0)) ||
            (value instanceof HTMLElement && !value.innerHTML.trim())
          ) {
            errors.push($t ? $t('validation.required') : defaultMessages.required);
          }
          break;
        case 'maxLength':
          if (value.length > parseInt(ruleValue)) {
            errors.push(
              $t
                ? $t('validation.max_length', { max: ruleValue })
                : defaultMessages.maxLength(ruleValue)
            );
          }
          break;
        case 'minLength':
          if (value.length < parseInt(ruleValue)) {
            errors.push(
              $t
                ? $t('validation.min_length', { min: ruleValue })
                : defaultMessages.minLength(ruleValue)
            );
          }
          break;
        case 'minItems':
          if (value.length < parseInt(ruleValue)) {
            errors.push(
              $t
                ? $t('validation.min_items', { min: ruleValue })
                : defaultMessages.minItems(ruleValue)
            );
          }
          break;
        case 'email':
        case 'emails':
          // Match leads.valid_email CHECK: local@domain.tld with TLD >= 2 chars.
          // The previous /^[^\s@]+@[^\s@]+\.[^\s@]+$/ allowed typos like
          // user@home.n.l which then failed lead creation in the API.
          // eslint-disable-next-line no-case-declarations
          const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
          // eslint-disable-next-line no-case-declarations
          const addresses =
            ruleName === 'emails'
              ? String(value || '')
                  .split(/[,;]/)
                  .map((email) => email.trim())
                  .filter(Boolean)
              : [value];
          if (addresses.some((email) => !emailPattern.test(email))) {
            errors.push($t ? $t('validation.email') : defaultMessages.email);
          }
          break;
        case 'min':
          if (Number(value) < parseInt(ruleValue)) {
            errors.push(
              $t ? $t('validation.min', { min: ruleValue }) : defaultMessages.min(ruleValue)
            );
          }
          break;
        case 'max':
          if (Number(value) > parseInt(ruleValue)) {
            errors.push(
              $t ? $t('validation.max', { max: ruleValue }) : defaultMessages.max(ruleValue)
            );
          }
          break;
        case 'requiredCheckbox':
          if (!Array.isArray(value) || value.length === 0) {
            errors.push($t ? $t('validation.required_checkbox') : defaultMessages.requiredCheckbox);
          }
          break;
        case 'kvkNl':
          if (value && !/^\d{8}$/.test(String(value).trim())) {
            errors.push($t ? $t('validation.kvk_nl') : defaultMessages.kvkNl);
          }
          break;
        case 'vatNl':
          if (value && !/^NL\d{9}B\d{2}$/i.test(String(value).replace(/\s/g, ''))) {
            errors.push($t ? $t('validation.vat_nl') : defaultMessages.vatNl);
          }
          break;
        default:
          break;
      }
    });
  }
  return errors.length > 0 ? errors.join(' ') : '';
}

export function validateNestedInput(value, rules) {
  // Handle array validation
  if (Array.isArray(value) && rules._each) {
    const errors = [];

    // Validate array itself if _self rules exist
    if (rules._self) {
      const arrayError = validateInput(value, rules._self);
      if (arrayError) return arrayError;
    }

    // Validate each item
    value.forEach((item, index) => {
      const itemErrors = {};

      // Check each field in the item
      Object.entries(rules._each).forEach(([field, fieldRules]) => {
        const error = validateInput(item[field], fieldRules);
        if (error) itemErrors[field] = error;
      });

      if (Object.keys(itemErrors).length > 0) {
        errors[index] = itemErrors;
      }
    });

    return errors.length > 0 ? errors : null;
  }

  // Handle regular validation
  return validateInput(value, rules);
}
